// ── 출퇴근 수정 모달 + 예정 근무지 설정 모달 ────────────────────────────────
import React, { useState } from 'react';
import { useToast } from '../../../components/Toast';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { styles, autoFormatTime } from './utils';
import { formatTimeTyping } from '../../../utils/timeInput';
import {
  stdModalHeaderBar, stdModalHeaderIconBox, stdModalHeaderTitle, stdModalHeaderSub, stdModalCloseBtn,
} from '../adminlayout/designStandard';

const { baseInputStyle, primaryBtn, grayBtn } = styles;

/**
 * 출퇴근 직접 수정 모달
 * Props:
 *   cell        — 해당 직원의 셀 데이터 (check_in, check_out, registered_ip_label, planned_work_location 등)
 *   date        — 날짜 (YYYY-MM-DD)
 *   ipAliases   — ip_aliases 목록 (부모에서 로드해 전달, 재요청 방지)
 *   onClose / onSave — 닫기/저장 콜백
 */
export function AttendanceEditModal({ cell, date, ipAliases, onClose, onSave }) {
  useBodyScrollLock(true);
  const [form, setFormState] = useState(() => ({
    check_in: cell?.check_in || '',
    check_out: cell?.check_out || '',
    // 이미 출근한 경우: IP 매핑 별칭(registered_ip_label) 우선 사용
    // 출근 전인 경우: 설정된 예정 근무지(planned_work_location) pre-select
    check_in_address: cell?.check_in
      ? (cell?.registered_ip_label || cell?.check_in_address || '')
      : (cell?.planned_work_location || ''),
    late_reason: cell?.late_reason || '',
  }));
  const [saving, setSaving] = useState(false);
  const { showToast } = useToast();

  function setField(field, value) {
    setFormState((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSave() {
    // 2026-04-29 긴급 fix: cell.user_id 누락 케이스 방어 — 후보 다중 시도
    const userIdToSend = cell?.user_id ?? cell?.userId ?? cell?.emp_id ?? cell?.id;
    if (userIdToSend === null || userIdToSend === undefined || userIdToSend === '') {
      // eslint-disable-next-line no-console
      console.error('[AttendanceEditModal] user_id 누락 — cell:', cell);
      showToast('직원 정보를 가져올 수 없습니다 (cell.user_id 누락). 새로고침 후 재시도해주세요.', 'error');
      return;
    }
    const checkIn = autoFormatTime(form.check_in);
    const checkOut = autoFormatTime(form.check_out);
    if (checkOut && !checkIn) {
      const ok = window.confirm(
        '퇴근시간만 입력되어 있습니다.\n\n급여와 주휴수당 계산은 출근시간이 있는 날짜를 출근일로 봅니다.\n실제 근무일이면 출근시간도 함께 입력해야 합니다.\n\n그래도 저장할까요?',
      );
      if (!ok) return;
    }
    setSaving(true);
    try {
      await onSave({
        user_id: userIdToSend,
        date,
        check_in:         checkIn               || null,
        check_out:        checkOut              || null,
        check_in_address: form.check_in_address  || null,
        late_reason:      form.late_reason        || null,
      });
      showToast('출퇴근 기록을 저장했습니다. 이미 계산된 급여월이면 월 급여 관리에서 재계산해야 급여에 반영됩니다.', 'success');
      onClose();
    } catch (err) {
      showToast(err.message || '저장 실패', 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card)', borderRadius: 12, width: 380, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
        <div style={stdModalHeaderBar(12)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={stdModalHeaderIconBox}>✏️</div>
            <div style={{ minWidth: 0 }}>
              <div style={stdModalHeaderTitle}>출퇴근 수정</div>
              <div style={stdModalHeaderSub}>{cell?.name} ({date})</div>
            </div>
          </div>
          <button onClick={onClose} style={stdModalCloseBtn}>×</button>
        </div>

        <div style={{ padding: '20px 28px 28px' }}>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>출근 시간 (HH:mm)</label>
          <input
            type="text" inputMode="numeric" placeholder="0930 또는 09:30"
            value={form.check_in}
            onChange={(e) => setField('check_in', formatTimeTyping(e.target.value))}
            onBlur={(e) => setField('check_in', autoFormatTime(e.target.value))}
            style={{ ...baseInputStyle, width: '100%' }}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>퇴근 시간 (HH:mm)</label>
          <input
            type="text" inputMode="numeric" placeholder="1800 또는 18:00"
            value={form.check_out}
            onChange={(e) => setField('check_out', formatTimeTyping(e.target.value))}
            onBlur={(e) => setField('check_out', autoFormatTime(e.target.value))}
            style={{ ...baseInputStyle, width: '100%' }}
          />
        </div>
        <div style={{ marginBottom: 12 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>근무지</label>
          <select value={form.check_in_address} onChange={(e) => setField('check_in_address', e.target.value)} style={{ ...baseInputStyle, width: '100%' }}>
            <option value="">— 선택 안 함 —</option>
            {/* 개인 IP (user_id 일치) + 통합 IP (user_id NULL) 둘 다 표시 — 2026-04-29 */}
            {ipAliases
              .filter((a) => {
                if (typeof a === 'string') return true; // 옛 응답 호환
                if (!a) return false;
                if (a.is_active === false) return false;
                if (a.user_id === null || a.user_id === undefined) return true; // 통합
                return Number(a.user_id) === Number(cell?.user_id); // 개인
              })
              .map((a, idx) => {
                const label = typeof a === 'string' ? a : a.label;
                if (!label) return null;
                return <option key={`${label}-${idx}`} value={label}>{label}</option>;
              })}
          </select>
        </div>
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>지각 사유</label>
          <input
            type="text" value={form.late_reason}
            onChange={(e) => setField('late_reason', e.target.value)}
            placeholder="지각 사유 (없으면 비워두세요)"
            style={{ ...baseInputStyle, width: '100%' }}
          />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={grayBtn}>취소</button>
          <button onClick={handleSave} disabled={saving} style={primaryBtn}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}

/**
 * 미래 날짜의 직원별 예정 근무지를 설정하는 모달
 * Props: { cell, date, ipAliases, onClose, onSave }
 */
export function PlannedLocationModal({ cell, date, ipAliases, onClose, onSave }) {
  useBodyScrollLock(true);
  const [workLocation, setWorkLocation] = useState(cell?.planned_work_location || '');
  const [saving, setSaving] = useState(false);
  const { showToast } = useToast();

  async function handleSave() {
    setSaving(true);
    try {
      await onSave({ user_id: cell.user_id, date, work_location: workLocation });
      onClose();
    } catch (err) {
      showToast(err.message || '저장 실패', 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card)', borderRadius: 12, width: 360, maxWidth: '95vw', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
        <div style={stdModalHeaderBar(12)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={stdModalHeaderIconBox}>📍</div>
            <div style={{ minWidth: 0 }}>
              <div style={stdModalHeaderTitle}>예정 근무지 설정</div>
              <div style={stdModalHeaderSub}>{cell?.name} ({date})</div>
            </div>
          </div>
          <button onClick={onClose} style={stdModalCloseBtn}>×</button>
        </div>

        <div style={{ padding: '20px 28px 28px' }}>
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>근무지</label>
          <select value={workLocation} onChange={(e) => setWorkLocation(e.target.value)} style={{ ...baseInputStyle, width: '100%' }}>
            {/* 빈 값 선택 시 기본 근무지(default_work_location)로 복원됨 */}
            <option value="">— 기본값 사용 ({cell?.planned_work_location ? '현재: ' + cell.planned_work_location : '미지정'}) —</option>
            {/* 개인 IP (user_id 일치) + 통합 IP (user_id NULL) 둘 다 표시 — 2026-04-29 */}
            {ipAliases
              .filter((a) => {
                if (typeof a === 'string') return true; // 옛 응답 호환
                if (!a) return false;
                if (a.is_active === false) return false;
                if (a.user_id === null || a.user_id === undefined) return true; // 통합
                return Number(a.user_id) === Number(cell?.user_id); // 개인
              })
              .map((a, idx) => {
                const label = typeof a === 'string' ? a : a.label;
                if (!label) return null;
                return <option key={`${label}-${idx}`} value={label}>{label}</option>;
              })}
          </select>
          <p style={{ margin: '6px 0 0', fontSize: 11, color: 'var(--text-muted)' }}>
            빈 값 선택 시 직원 기본 근무지로 복원됩니다.
          </p>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={grayBtn}>취소</button>
          <button onClick={handleSave} disabled={saving} style={primaryBtn}>
            {saving ? '저장 중...' : '저장'}
          </button>
        </div>
        </div>
      </div>
    </div>
  );
}
